import SliderHeader from '@/components/arguments/argumentOptionsSlider/SliderHeader.jsx';
import { HiOutlineSparkles } from '@/components/react-icons/main';
import { getAuditLogs } from '../../redux/actions/gravityActions.js';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Panel from '@/components/panel/Panel.jsx';
import { closeAuditLogSlider } from '@/redux/actions/uiActions/setAuditLogSlider';
import routes from '@/routes.js';
import workflowScreenSelectors from '@/screens/workflows/redux/selectors/workflowScreenSelectors.js';
import appScreenSelectors from '@/screens/apps/redux/selectors/appScreenSelectors.js';
import credentialScreenSelectors from '@/screens/apps/redux/selectors/credentialScreenSelectors.js';
import AuditLogSliderItem from './auditLogSlider/AuditLogSliderItem.jsx';
import PaginationButtons from '../PaginationButtons.jsx';
import SearchBar from '@/components/SearchBar.jsx';

const objectSelectors = {
  workflows: workflowScreenSelectors.activeWorkflow,
  apps: appScreenSelectors.activeApp,
  credentials: credentialScreenSelectors.activeCredential
};

const objectLabels = {
  workflows: 'workflow',
  apps: 'app',
  credentials: 'credential'
};

const AuditLogSlider = () => {
  const activeRouteId = useSelector((state) => state.ui.activeRouteId);
  const activeRouteSlug = routes.find((n) => n.id === activeRouteId)?.routeId;
  const settings = useSelector((state) => state?.ui?.auditLogSlider);
  const open = settings?.open ?? false;
  const activeObjectId = useSelector((state) => {
    const selector = objectSelectors[activeRouteSlug];
    return !selector ? null : selector(state)?.id;
  });
  const activeObjectCredentialId = useSelector((state) => {
    const selector = objectSelectors[activeRouteSlug];
    return !selector ? null : selector(state)?.credentialId;
  });
  const auditLogs = useSelector((state) => state.workflow.auditLogs?.data) || [];
  const auditLogsMeta = useSelector((state) => state.workflow.auditLogs?.meta);
  const total = auditLogsMeta?.total || auditLogs?.length || 0;
  const limit = auditLogsMeta?.limit || 20;
  const initialPage = auditLogsMeta?.page || 0;

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetchedAuditLogs, setHasFetchedAuditLogs] = useState(null);
  const [search, setSearch] = useState('');

  const handleClose = () => {
    closeAuditLogSlider();
    setHasFetchedAuditLogs(null);
    setError(null);
    setIsLoading(false);
  };

  const fetchAuditLogs = ({ page = 0, search: searchQuery } = {}) => {
    if (!activeObjectId) {
      return;
    }

    setIsLoading(true);
    setError(null);

    const type = activeRouteSlug === 'apps' ? 'app' : activeRouteSlug === 'credentials' ? 'credential' : activeRouteSlug === 'workflows' ? 'workflow' : undefined;

    getAuditLogs(
      {
        id: activeRouteSlug === 'credentials' ? activeObjectCredentialId : activeObjectId,
        page,
        limit,
        search: searchQuery ?? search,
        type
      },
      (response) => {
        setIsLoading(false);
        if (!response) {
          setError('No audit logs found');
        }
      }
    );
  };

  useEffect(() => {
    if (open && activeObjectId && hasFetchedAuditLogs !== activeObjectId) {
      fetchAuditLogs();
      setHasFetchedAuditLogs(activeObjectId);
    }
  }, [open, activeObjectId, activeObjectCredentialId, hasFetchedAuditLogs]);

  return (
    <div className={`comp-argument-option-slider audit-log-slider ${open ? 'open' : ''}`}>
      <div className={'backing'} onClick={handleClose} />
      <div className={'content'}>
        <SliderHeader title="Audit Logs" onClose={handleClose} />
        <div className="slider-body">
          <div className="audit-log-content">
            <div className="audit-log-search">
              <SearchBar
                placeholder={'Search audit logs'}
                value={search}
                onSearch={(val) => {
                  setSearch(val);
                  fetchAuditLogs({ page: 0, search: val });
                }}
                delay={300}
                variant="header borderless no-padding"
                icon={null}
              />
            </div>
            {!isLoading && !error && auditLogs.length > 0 && (
              <div className="audit-log-list">
                {auditLogs.map((auditLog) => (
                  <AuditLogSliderItem key={auditLog.id} auditLog={auditLog} />
                ))}
              </div>
            )}

            {isLoading && (
              <div className="audit-log-empty-state">
                <Panel.EmptyState title={'Loading Audit Logs...'} description={`This may take a moment`} icon={HiOutlineSparkles} loading={true} actions={[]} />
              </div>
            )}

            {error === 'notFound' && !isLoading && (
              <div className="audit-log-empty-state">
                <Panel.EmptyState title={'No Audit Logs Available'} description={`Audit logs will be generated as you make changes to your ${objectLabels[activeRouteSlug] || 'object'}`} icon={HiOutlineSparkles} loading={false} actions={[]} />
              </div>
            )}

            {!error && !isLoading && auditLogs.length === 0 && (
              <div className="audit-log-empty-state">
                <Panel.EmptyState title={'No Audit Logs Available'} description={`Audit logs will be generated as you make changes to your ${objectLabels[activeRouteSlug] || 'object'}`} icon={HiOutlineSparkles} loading={false} actions={[]} />
              </div>
            )}

            {error && error !== 'notFound' && !isLoading && (
              <div className="audit-log-empty-state">
                <Panel.EmptyState title="Error Loading Audit Logs" description={error} icon={HiOutlineSparkles} loading={false} actions={[{ label: 'Retry', onClick: fetchAuditLogs }]} />
              </div>
            )}
            <PaginationButtons total={total} limit={limit} initialPage={initialPage} onPageChange={(newPage) => fetchAuditLogs({ page: newPage })} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditLogSlider;
